/**
 * LeanChart - entry point for LightChart
 */
import React, { Suspense } from 'react';
import { connect } from 'react-redux';
import { ITicker, enums } from '@kattana-io/common';
import { LightweightChart } from '@kattana-io/light-charts';
import { actionChangeTicker, actionSwitchPrimary } from '../../actions/grid';
import { actionSetTimezone } from '../../actions/timezone';
import { IGridItem, IGridNet } from '../../reducers/grid';
import { getDatafeed } from '../TradingViewChart';
import { IStore } from '../../reducers';
import { actionOpenTickers, actionOpenTimeframe } from '../../actions/modal';
import PairIcons from '../../molecules/PairIcons';
import { switchCurrentTicker } from '../../Authlogic';
import { pairConvertable } from '../TradingViewChart/datafeeds/helpers/symbols';

interface LightweightChartState {
  datafeed: any;
  loading: boolean;
  primary: boolean;
  localExchange?: string;
  ticker?: ITicker;
  base?: string;
  quote?: string;
  resolution: string;
}

interface LightWeightChartProps {
  actionSwitchPrimary: (payload: ITicker) => void;
  itemKey: string;
  currentTickerId: number;
  grid: IGridNet;

  symbolId: number;
  exchange: string;
  base: string;
  quote: string;
  address: string;
  mode: string;
  orders: any;
  width: number;
  height: number;
  pricePrecision: number;
  markets: any;
  timeframeExcludes: string[];
  actionCancelOrders: Function;
  actionShowModal?: Function;
  isMobile: boolean;
  datafeed?: any;
  options?: any;
  symbolName?: string;
  onExchangeChange?: any;
  maxWidth?: number;
  maxHeight?: number;
  onChartClick?: () => void;
}

interface IHash {
  [details: string]: string;
}

export const dexResolutionMapping: IHash = {
  [enums.ETimeframes.second]: '1s',
  [enums.ETimeframes.fiveSeconds]: '5s',
  [enums.ETimeframes.tenSeconds]: '10s',
  [enums.ETimeframes.minute]: '1m',
  [enums.ETimeframes.threeMinutes]: '3m',
  [enums.ETimeframes.fiveMinutes]: '5m',
  [enums.ETimeframes.fifteenMinutes]: '15m',
  [enums.ETimeframes.thirtyMinutes]: '30m',
  [enums.ETimeframes.hour]: '1h',
  [enums.ETimeframes.twoHours]: '2h',
  [enums.ETimeframes.fourHours]: '4h',
  [enums.ETimeframes.sixHours]: '6h',
  [enums.ETimeframes.eightHours]: '8h',
  [enums.ETimeframes.twelveHours]: '12h',
};

export const cexResolutionMapping: IHash = {
  [enums.ETimeframes.minute]: '1m',
  [enums.ETimeframes.fiveMinutes]: '5m',
  [enums.ETimeframes.fifteenMinutes]: '15m',
  [enums.ETimeframes.thirtyMinutes]: '30m',
  [enums.ETimeframes.hour]: '1h',
  [enums.ETimeframes.day]: '1d',
  [enums.ETimeframes.week]: '1w',
};

const reverseObj = (obj: Record<string, number | string>) =>
  Object.entries(obj).reduce((ret: IHash, entry) => {
    const [key, value] = entry;
    ret[value] = key;
    return ret;
  }, {});

const chartOptions = {
  alignLabels: false,
  timeScale: {
    fixLeftEdge: false,
    lockVisibleTimeRangeOnResize: false,
    rightBarStaysOnScroll: false,
    borderVisible: true,
    borderColor: '#2b2b43',
    visible: true,
    timeVisible: true,
    secondsVisible: false,
  },
  priceScale: {
    position: 'right',
    mode: 1,
    autoScale: false,
    invertScale: false,
    alignLabels: false,
    borderVisible: false,
    borderColor: '#2b2b43',
    entireTextOnly: false,
    scaleMargins: {
      bottom: 0.1,
      top: 0.2,
    },
  },

  localization: {
    locale: 'en-US',
  },
  handleScale: true,
};

class LeanChart extends React.Component<
  LightWeightChartProps & any,
  LightweightChartState
> {
  remountTimer: any;

  constructor(props: any) {
    super(props);

    this.remountTimer = 0;
    const { itemKey, grid, timeframeExcludes } = this.props;
    const { primary, ticker, exchange } = grid[itemKey] as IGridItem;
    const canConvert = pairConvertable(
      ticker.chain,
      ticker.flipped ? ticker.base_address : ticker.quote_address
    );

    this.state = {
      datafeed: getDatafeed(
        exchange,
        ticker.chain,
        canConvert,
        timeframeExcludes
      ),
      loading: false,
      ticker,
      primary,
      resolution: '60',
    };
  }

  handleGrid(nextProps: LightWeightChartProps) {
    const { primary } = this.state;
    const { grid, itemKey } = nextProps;
    const {
      primary: newPrimary,
      exchange,
      ticker,
    } = grid[itemKey] as IGridItem;

    if (newPrimary != primary) {
      this.setState({
        primary: newPrimary,
      });
    }

    return {
      primary: newPrimary,
      exchange,
      ticker,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps: LightWeightChartProps) {
    const { primary, exchange, ticker } = this.handleGrid(nextProps);
    const { itemKey, grid, timeframeExcludes } = this.props;
    const { ticker: currentTicker } = grid[itemKey] as IGridItem;
    const canConvert = pairConvertable(
      ticker.chain,
      ticker.flipped ? ticker.base_address : ticker.quote_address
    );

    /**
     * If primary or symbol changes - reload
     */
    if (
      (primary && exchange !== this.state.localExchange) ||
      ticker.id !== currentTicker.id
    ) {
      /**
       * Remount when exchange changed
       */
      this.setState({
        loading: false,
        datafeed: getDatafeed(
          exchange,
          ticker.chain,
          canConvert,
          timeframeExcludes
        ),
        localExchange: exchange,
      });
    }

    /**
     * If pricing plan changes - reload
     */
    if (timeframeExcludes !== nextProps.timeframeExcludes) {
      /**
       * Remount when timeframe limitations changed
       */
      this.setState({
        loading: false,
        resolution: '60',
      });
    }

    /**
     * React to layout event
     */
    if (
      this.props.maxWidth !== nextProps.maxWidth ||
      this.props.maxHeight !== nextProps.maxHeight
    ) {
      this.setState({ loading: true }, () => {
        this.remountTimer = setTimeout(() => {
          this.setState({ loading: false });
        }, 300);
      });
    }
  }

  onExchangeChangeHandler = (exchange: string): void => {
    this.props.actionChangeTickersExchange({
      exchange,
      tickerKey: this.props.itemKey,
    });
  };

  /**
   * Check if current chart represents current state
   * If yes - bubble
   * If not - switch current
   */
  onChartClick = (e: any) => {
    const currentGridItem: IGridItem = this.props.grid[this.props.itemKey];
    if (currentGridItem && !currentGridItem.primary) {
      const ticker = currentGridItem.ticker;
      const [base, quote] = ticker.name.split('/');

      this.props.actionSwitchPrimary({ newPrimaryKey: this.props.itemKey });
      switchCurrentTicker({
        ...ticker,
        base,
        quote,
        exchange: currentGridItem.exchange,
      });
    }
  };

  render() {
    const { maxWidth, maxHeight, itemKey, grid, timezone, timeframeExcludes } =
      this.props;
    const { datafeed, loading } = this.state;
    const { primary, ticker, exchange }: IGridItem = grid[itemKey];

    const isCex = ticker.kind === enums.ETickerKind.CEX;

    const resolutionMapping = isCex
      ? cexResolutionMapping
      : dexResolutionMapping;
    const resolutionMappingReversed = reverseObj(resolutionMapping);

    if (maxWidth === 0) {
      return <div />;
    }

    if (!datafeed) {
      return <div />;
    }

    if (loading) {
      return <div />;
    }

    return (
      <Suspense fallback={null}>
        <LightweightChart
          primary={primary}
          active={primary}
          key={exchange}
          itemKey={itemKey}
          exchange={exchange}
          width={maxWidth}
          height={maxHeight}
          datafeed={datafeed}
          options={chartOptions}
          onChartClick={this.onChartClick}
          ticker={ticker}
          resolutionMapping={resolutionMapping}
          resolution={this.state.resolution}
          onSwitchCurrent={this.props.actionSwitchCurrent}
          onSwitchPrimary={this.props.actionSwitchPrimary}
          onTimezoneChange={this.props.actionSetTimezone}
          onChangeTicker={this.props.actionChangeTicker}
          onOpenTickersHandler={this.props.actionOpenTickers}
          timezone={timezone}
          onOpenResolutionsPicker={() => {
            this.props.actionOpenTimeframe({
              selected: resolutionMapping[this.state.resolution],
              onSelect: (newValue: string) => {
                this.setState({
                  resolution: resolutionMappingReversed[newValue],
                });
              },
              isCex: isCex,
              excludes: timeframeExcludes,
            });
          }}
          pairIconsRender={() => {
            return (
              <PairIcons
                chain={exchange}
                pair={ticker.name}
                base_address={ticker.base_address}
                quote_address={ticker.quote_address}
                base_slug={(ticker as any).base_slug}
                quote_slug={(ticker as any).quote_slug}
              />
            );
          }}
        />
      </Suspense>
    );
  }
}

const mapStateToProps = (state: IStore) => ({
  exchange: state.current.exchange,
  grid: state.grid.net,
  currentTickerId: state.current.id,
  timezone: state.timezone.current,
  timeframeExcludes: state.features.features.timeframeExcludes,
});

export default connect(mapStateToProps, {
  actionSwitchPrimary,
  actionChangeTicker,
  actionOpenTickers,
  actionOpenTimeframe,
  actionSetTimezone,
})(LeanChart);
